import React, { useState, useRef, useEffect } from 'react';
import { 
  Container, 
  Grid, 
  Card, 
  CardContent, 
  Typography, 
  Table, 
  TableBody, 
  TableCell, 
  TableContainer, 
  TableHead, 
  TableRow, 
  Paper, 
  useTheme, 
  Box,
  IconButton,
  Modal
} from '@mui/material';
import MenuBookIcon from '@mui/icons-material/MenuBook';
import ArticleIcon from '@mui/icons-material/Article';
import CloudDownloadIcon from '@mui/icons-material/CloudDownload';
import SearchIcon from '@mui/icons-material/Search';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import LinkIcon from '@mui/icons-material/Link';
import ContentCutIcon from '@mui/icons-material/ContentCut';
import ForceGraph from 'force-graph';
import { extendedKnowledgeGraphData as knowledgeGraphData } from './knowledgeGraphData';

// Import text content
import { documentsTexts } from '../texts/documents';

// Icon mapping
const iconMap = {
  'ArticleIcon': ArticleIcon,
  'MenuBookIcon': MenuBookIcon,
  'ContentCutIcon': ContentCutIcon
};

const DocumentsDashboard = () => {
  const theme = useTheme();
  const graphRef = useRef();
  const [modalOpen, setModalOpen] = useState(false);
  const [modalContent, setModalContent] = useState({ title: '', description: '' });
  const { dashboard: dashboardTexts } = documentsTexts;

  const handleOpenModal = (title) => {
    setModalContent({ title, description: dashboardTexts.modal.descriptions[title] });
    setModalOpen(true);
  };
  const handleCloseModal = () => setModalOpen(false);

  useEffect(() => {
    if (graphRef.current) {
      const graphContainer = graphRef.current;
      
      // Clear any previous instances
      while (graphContainer.firstChild) {
        graphContainer.removeChild(graphContainer.firstChild);
      }

      // Create a reference to the graph data that we can manipulate in callbacks
      const graphData = {...knowledgeGraphData};
      // Initialize with hovered state for all links set to false
      graphData.links = graphData.links.map(link => ({...link, hovered: false}));
      
      // Initialize the force graph
      const graph = ForceGraph()
        (graphContainer)
        .graphData(graphData)
        .backgroundColor('#f5f5f5')
        .linkColor(() => '#999')
        .linkWidth(link => link.hovered ? 2 : 1)
        .nodeLabel(node => `${node.name}${node.fullName ? ': ' + node.fullName : ''}`)
        .nodeColor(node => node.color)
        .nodeVal('val')
        .linkDirectionalArrowLength(6)
        .linkDirectionalArrowRelPos(1)
        .linkLabel(link => link.label)
        .linkCanvasObjectMode(() => 'after')
        .linkCanvasObject((link, ctx, globalScale) => {
          // Don't draw if not hovered
          if (!link.hovered) return;

          const start = link.source;
          const end = link.target;
          
          // Calculate label position (mid-point)
          const textPos = Object.assign({}, {
            x: start.x + (end.x - start.x) / 2,
            y: start.y + (end.y - start.y) / 2
          });
          
          const label = link.label;
          
          // Draw label
          ctx.save();
          const fontSize = 12/globalScale;
          ctx.font = `${fontSize}px Sans-Serif`;
          
          // Draw background
          const textWidth = ctx.measureText(label).width;
          const bckgDimensions = [textWidth, fontSize].map(n => n + fontSize * 0.2);
          ctx.fillStyle = 'rgba(255, 255, 255, 0.9)';
          ctx.strokeStyle = '#666';
          ctx.lineWidth = 0.5;
          const x = textPos.x - bckgDimensions[0] / 2;
          const y = textPos.y - bckgDimensions[1] / 2;
          ctx.fillRect(x, y, ...bckgDimensions);
          ctx.strokeRect(x, y, ...bckgDimensions);
          
          // Draw text
          ctx.textAlign = 'center';
          ctx.textBaseline = 'middle';
          ctx.fillStyle = '#444';
          ctx.fillText(label, textPos.x, textPos.y);
          ctx.restore();
        })
        .onLinkHover(link => {
          // Store hovered link as an attribute
          if (link) {
            graphData.links.forEach(l => l.hovered = false);
            if (link) link.hovered = true;
          }
        })
        .nodeCanvasObject((node, ctx, globalScale) => {
          const label = node.name;
          const fontSize = 12/globalScale;
          ctx.font = `${fontSize}px Sans-Serif`;
          const textWidth = ctx.measureText(label).width;
          const bckgDimensions = [textWidth, fontSize].map(n => n + fontSize * 0.2);

          // Draw node background
          ctx.fillStyle = node.color;
          ctx.fillRect(node.x - bckgDimensions[0] / 2, node.y - bckgDimensions[1] / 2, ...bckgDimensions);

          // Draw text
          ctx.textAlign = 'center';
          ctx.textBaseline = 'middle';
          ctx.fillStyle = 'white';
          ctx.fillText(label, node.x, node.y);
        })
        .onNodeClick(node => {
          // Center view on node
          graph.centerAt(node.x, node.y, 1000);
          graph.zoom(2, 2000);
        });

      // Adjust size on window resize
      const resizeGraph = () => {
        graph.width(graphContainer.clientWidth);
        graph.height(350);
      };
      
      window.addEventListener('resize', resizeGraph);
      resizeGraph();
      
      // Initial zoom to fit
      setTimeout(() => graph.zoomToFit(400), 500);
      
      return () => {
        window.removeEventListener('resize', resizeGraph);
      };
    }
  }, []);
  
  return (
    <Container maxWidth="1500px" width="100%" sx={{ minHeight: 'calc(100vh - 116px)', bgcolor: 'background.paper', py: 4 }}>
      {/* Modal for card info */}
      <Modal
        open={modalOpen}
        onClose={handleCloseModal}
        aria-labelledby="info-modal-title"
        aria-describedby="info-modal-description"
      >
        <Box
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: 340,
            bgcolor: 'background.default',
            borderRadius: 3,
            boxShadow: 24,
            p: 4,
            outline: 0,
            textAlign: 'center',
          }}
        >
          <Typography id="info-modal-title" variant="h4" sx={{ mb: 2 }}>
            {modalContent.title}
          </Typography>
          <Typography id="info-modal-description" color="text.secondary" variant='body2'>
            {modalContent.description}
          </Typography>
        </Box>
      </Modal>

      <Typography
        variant="h4"
        component="h1"
        sx={{ mb: 6, textAlign: 'left', color: 'text.secondary' }}
      >
        {dashboardTexts.title}
      </Typography>

      {/* Stats Cards */}
      <Grid container spacing={4} sx={{ mb: 4, justifyContent: 'center' }}>
        {dashboardTexts.stats.map((stat) => {
          const IconComponent = iconMap[stat.icon];
          return (
            <Grid item xs={12} md={4} key={stat.title}>
              <Card 
                elevation={2}
                sx={{ 
                  borderRadius: 4,
                  height: '100%',
                  bgcolor: 'background.default',
                  transition: 'transform 0.2s ease-in-out',
                  position: 'relative',
                  minWidth: 150,
                }}
              >
                {/* Info icon button */}
                <IconButton
                  size="small"
                  sx={{
                    position: 'absolute',
                    top: 12,
                    right: 12,
                    color: 'grey.500',
                    zIndex: 2,
                  }}
                  aria-label={`Información sobre ${stat.title}`}
                  onClick={() => handleOpenModal(stat.title)}
                >
                  <InfoOutlinedIcon />
                </IconButton>
                <CardContent sx={{ 
                  display: 'flex', 
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  textAlign: 'center',
                  p: 3
                }}>
                  <Box 
                    sx={{ 
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      bgcolor: stat.color,
                      borderRadius: '50%',
                      p: 2,
                      mb: 2,
                      color: '#fff',
                      width: 64,
                      height: 64
                    }}
                  >
                    <IconComponent />
                  </Box>
                  <Typography variant="h3" component="div" sx={{ fontWeight: 700, color: stat.color, mb: 1 }}>
                    {stat.value}
                  </Typography>
                  <Typography variant="subtitle1" color="text.secondary">
                    {stat.title}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          );
        })}
      </Grid>

      {/* Search card */}
      <Card
        elevation={2}
        sx={{
          borderRadius: 4,
          bgcolor: 'background.default',
          mb: 4,
          p: 3,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          flexDirection: { xs: 'column', sm: 'row' },
          gap: 2
        }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            width: '100%',
            borderRadius: 1,
            bgcolor: 'background.default',
            p: 1
          }}
        >
          <input
            type="text"
            placeholder={dashboardTexts.search.placeholder}
            style={{
              flex: 1,
              border: 'none',
              outline: 'none',
              padding: '8px 12px',
              borderRadius: 4,
              backgroundColor: 'transparent',
              color: theme.palette.text.primary,
            }}
          />
          <IconButton
            sx={{
              backgroundColor: theme.palette.primary.main,
              color: '#fff',
              padding: '8px',
              marginLeft: 1,
              transition: 'background-color 0.2s ease-in-out',
              '&:hover': {
                backgroundColor: theme.palette.primary.dark,
              }
            }}
          >
            <SearchIcon />
          </IconButton>
        </Box>
      </Card>
        

      {/* Documents Table & Chart Row */}
      <Grid container spacing={2} sx={{ mb: 4 }}>
        <Grid item size={{ xs: 12, sm: 6 }}>
          <Card 
            elevation={2}
            sx={{ 
              borderRadius: 4,
              bgcolor: 'background.default',
              height: '100%',
              position: 'relative',
            }}
          >
            {/* Info icon button for documents table */}
            <IconButton
              size="small"
              sx={{
                position: 'absolute',
                top: 12,
                right: 12,
                color: 'grey.500',
                zIndex: 2,
              }}
              aria-label="Información sobre Base de Contextual"
              onClick={() => handleOpenModal('Base de Contextual')}
            >
              <InfoOutlinedIcon />
            </IconButton>
            <CardContent>
              <Typography variant="h5" component="div" sx={{ mb: 3, color: 'text.secondary' }}>
                {dashboardTexts.documentsTable.title}
              </Typography>
              <TableContainer component={Paper} sx={{ boxShadow: 'none', maxHeight: 400 }}>
                <Table stickyHeader>
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 'bold', bgcolor: 'grey.50' }}>{dashboardTexts.documentsTable.headers.title}</TableCell>
                      <TableCell sx={{ fontWeight: 'bold', bgcolor: 'grey.50' }}>{dashboardTexts.documentsTable.headers.source}</TableCell>
                      <TableCell sx={{ fontWeight: 'bold', bgcolor: 'grey.50' }} align="right">{dashboardTexts.documentsTable.headers.link}</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {dashboardTexts.documentsTable.documents.map((doc, idx) => (
                      <TableRow key={doc.title} hover>
                        <TableCell sx={{ whiteSpace: 'normal', wordBreak: 'break-word'}}>{doc.title}</TableCell>
                        <TableCell sx={{ whiteSpace: 'normal', wordBreak: 'break-word'}}>{doc.source}</TableCell>
                        <TableCell align="right">
                          <IconButton size="small" aria-label={`Enlace al documento ${idx + 1}`}
                            onClick={() => window.open(doc.link, '_blank')}
                            sx={{ color: 'primary.main' }}>
                            <LinkIcon />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </CardContent>
          </Card>
        </Grid>
        <Grid item size={{ xs: 12, sm: 6 }}>
          <Card 
            elevation={2}
            sx={{ 
              borderRadius: 4,
              bgcolor: 'background.default',
              height: '100%',
              position: 'relative',
            }}
          >
            {/* Info icon button for knowledge graph */}
            <IconButton
              size="small"
              sx={{
                position: 'absolute',
                top: 12,
                right: 12,
                color: 'grey.500',
                zIndex: 2,
              }}
              aria-label="Información sobre Diagrama de Conocimiento"
              onClick={() => handleOpenModal('Diagrama de Conocimiento')}
            >
              <InfoOutlinedIcon />
            </IconButton>
            <CardContent sx={{
              display: 'flex',
              flexDirection: 'column',
              height: '100%',
            }}>
              <Typography variant="h5" component="div" sx={{ mb: 3, color: 'text.secondary' }}>
                {dashboardTexts.knowledgeGraph.title}
              </Typography>
              <Box 
                ref={graphRef} 
                sx={{ 
                  height: 350, 
                  border: '1px solid rgba(0,0,0,0.1)', 
                  borderRadius: 1,
                  overflow: 'hidden'
                }}
              />
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Container>
  );
};

export default DocumentsDashboard;
